import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { SectionContainer } from "@/components/marketing/SectionContainer";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { onboardingApi, ApiError } from "@/lib/api";
import { usePageMeta } from "./usePageMeta";

// Post-register card. The verification link in the email points to
// /onboarding/verify?token=... on this same origin — the wizard takes over
// from there.
export function RegisterCheckEmail() {
  usePageMeta({
    title: "Check your inbox",
    description: "We sent a verification link to finish creating your VirtualDesk account.",
  });

  const [params] = useSearchParams();
  const queryEmail = params.get("email");
  const devVerifyURL = params.get("dev_verify_url");
  const storeEmail = useOnboardingStore((s) => s.email);
  const onboardingId = useOnboardingStore((s) => s.onboardingId);
  const email = queryEmail ?? storeEmail ?? "your inbox";

  const [resending, setResending] = useState(false);
  // Honest state machine. The page lands here only after a successful
  // backend Register; we always start in "sent" (or "dev" when the dev
  // verify URL is in the query). The Resend button can transition us into
  // "failed" so we never claim a re-send happened when it didn't.
  type Status =
    | { kind: "sent" }
    | { kind: "dev"; verifyURL: string }
    | { kind: "failed"; description: string };
  const initialStatus: Status = devVerifyURL
    ? { kind: "dev", verifyURL: devVerifyURL }
    : { kind: "sent" };
  const [status, setStatus] = useState<Status>(initialStatus);

  const resend = async () => {
    if (!onboardingId) {
      toast.error("Please restart registration");
      return;
    }
    setResending(true);
    try {
      const res = await onboardingApi.resendVerification(onboardingId);
      if (res?.email_sent) {
        setStatus({ kind: "sent" });
        toast.success("Verification email re-sent", {
          description: `New link sent to ${email}.`,
        });
      } else if (res?.dev_verify_url) {
        setStatus({ kind: "dev", verifyURL: res.dev_verify_url });
        toast.message("Dev mode — email not actually sent", {
          description: `Provider ${res.email_provider}. Use the verify URL inline.`,
        });
      } else {
        const description = "We couldn't deliver the email. Please try again in a moment.";
        setStatus({ kind: "failed", description });
        toast.error("Couldn't resend", { description });
      }
    } catch (err) {
      const ae = err as ApiError;
      console.error("resendVerification", ae);
      const description =
        ae.code === "email_provider_unavailable"
          ? "Verification email service is temporarily down. Please try again in a few minutes or contact support."
          : ae.code === "email_send_failed"
          ? "We couldn't deliver the email. Please try again in a moment."
          : ae.status === 0
          ? "We couldn't reach VirtualDesk. Check your connection and try again."
          : ae.message || "Please try again.";
      setStatus({ kind: "failed", description });
      toast.error("Couldn't resend", { description });
    } finally {
      setResending(false);
    }
  };

  const headline =
    status.kind === "sent"
      ? "We sent you a verification link."
      : status.kind === "dev"
      ? "Dev mode — verification link below."
      : "We couldn't send your verification email.";
  const body =
    status.kind === "sent" ? (
      <p className="mt-1 text-sm text-brand-ink/70">
        Sent to <span className="font-mono text-brand-ink">{email}</span>. Click the
        link to continue. The link expires in 24 hours.
      </p>
    ) : status.kind === "dev" ? (
      <p className="mt-1 text-sm text-brand-ink/70">
        Email delivery is disabled in this environment. Open the link below to
        finish verifying <span className="font-mono text-brand-ink">{email}</span>.
      </p>
    ) : (
      <p className="mt-1 text-sm text-signal-failed">{status.description}</p>
    );

  const iconBg =
    status.kind === "failed"
      ? "bg-signal-failed/15 text-signal-failed"
      : "bg-brand-violet/15 text-brand-violet";

  return (
    <SectionContainer>
      <div className="mx-auto max-w-[480px]">
        <div className="text-center">
          <p className="text-sm font-semibold uppercase tracking-wider text-brand-violet">
            Step 1 of 4
          </p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl tracking-wide leading-[0.95] text-brand-ink">
            {status.kind === "failed" ? "Email not sent." : "Check your inbox."}
          </h1>
        </div>

        <div className="mt-10 rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8">
          <div className="flex items-center gap-3">
            <div className={`rounded-md p-2 ${iconBg}`}>
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-brand-ink">{headline}</h2>
              {body}
            </div>
          </div>

          {status.kind === "dev" ? (
            <a
              href={status.verifyURL}
              className="mt-6 block rounded-md border border-brand-cyan/40 bg-brand-cyan/5 px-4 py-3 text-xs font-mono text-brand-cyan break-all hover:bg-brand-cyan/10"
            >
              {status.verifyURL}
            </a>
          ) : status.kind === "sent" ? (
            <div className="mt-6 rounded-md border border-border bg-surface-2/40 px-4 py-3 text-xs text-brand-ink/60">
              Click the link to continue setup — pick your subdomain, hire your
              first agent, and you're in.
            </div>
          ) : null}

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={resend}
              disabled={resending}
              className="border-white/20 text-brand-ink hover:bg-white/5"
            >
              <RefreshCw className={resending ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />
              {resending ? "Resending…" : "Resend verification email"}
            </Button>
            <Link
              to="/register"
              className="text-sm text-brand-ink/60 hover:text-brand-ink"
            >
              Use a different email
            </Link>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-brand-ink/50">
          Didn't get it? Check spam, or email{" "}
          <a href="mailto:hello@virtualdesk.ai" className="underline hover:text-brand-ink/80">
            hello@virtualdesk.ai
          </a>
          .
        </p>
      </div>
    </SectionContainer>
  );
}
